import { lcCoachingApi } from '@/api/lcCoaching'
import { lcReviewNoteApi, type ReviewNoteView } from '@/api/lcReviewNote'
import type { BuildupGrade, EssentialCheck, LessonProcessDetailView, ManualGradeCredit, WorkbookGradeView } from '@/types/coaching'
import { isRequiredBuildupGradeItem, REQUIRED_WORKBOOK_PROBLEM_NOS } from '../lessonOperationStatus'
import { normalizePauseCurrent, pauseRowsOf, reviewNoteRowsOf, type GradingSheetRow } from '../grading-sheet/gradingRows'
import type { HandheldDraftStore } from './handheldDraftStore'

export type HandheldSection = 'buildup' | 'blockQuiz' | 'reviewNote' | 'workbook' | 'essentialCheck'
export const HANDHELD_SECTIONS: HandheldSection[] = ['blockQuiz', 'buildup', 'reviewNote', 'workbook', 'essentialCheck']
type Target =
  | { kind: 'buildup'; configComponentId: string; step: string; itemNo: number }
  | { kind: 'blockQuiz'; configComponentId: string; quizId: string; pauseTimeMs: number }
  | { kind: 'reviewNote'; setId: number; quizId: string }
  | { kind: 'workbook'; itemNo: number }
  | { kind: 'essentialCheck'; checkNo: number }

export interface HandheldRow {
  key: string
  section: HandheldSection
  no: number
  label?: string | null
  answer?: string | null
  answerImages?: string[]
  current: number | null
  writable: boolean
  allowPartial: boolean
  gradingRow?: GradingSheetRow
  target: Target
}
const buildupCredit = (grade?: BuildupGrade | null) => grade === 'O' ? 1 : grade === 'X' ? 0 : grade === 'TRIANGLE' ? 0.5 : null
const toBuildupGrade = (credit: number): BuildupGrade => credit === 1 ? 'O' : credit === 0 ? 'X' : 'TRIANGLE'
function asCredit(value: number): ManualGradeCredit {
  if (value !== 0 && value !== 0.5 && value !== 1) throw new Error('Invalid grade credit')
  return value
}

/** Uses only the selected attempt's component data; never falls back to initial progress. */
export function buildHandheldRows({ lesson, attemptId, workbook, checks, reviewNotes }: {
  lesson: LessonProcessDetailView
  attemptId: number | null
  workbook: WorkbookGradeView[]
  checks: EssentialCheck[]
  reviewNotes: ReviewNoteView[]
}): HandheldRow[] {
  const rows: HandheldRow[] = []
  const components = [...(attemptId == null ? lesson.components : lesson.reviewComponents ?? [])].sort((a, b) => a.sortOrder - b.sortOrder)
  for (const component of components) {
    if (component.type === 'buildup' && component.buildup && component.buildup.componentProgressId > 0) {
      for (const step of component.buildup.steps) {
        const stepNo = step.step.match(/\d+/)?.[0]
        const modelAnswer = stepNo === '4' ? step.items.find(item => item.number === 2)?.answer : null
        for (const item of step.items.filter(i => isRequiredBuildupGradeItem(step.step, i.number))) {
          rows.push({
            key: `buildup:${component.configComponentId}:${step.step}:${item.number}`, section: 'buildup', no: item.number,
            label: `${component.label} · ${step.title || step.step}`, answer: modelAnswer?.trim() ? modelAnswer : item.answer,
            answerImages: stepNo === '2' ? item.descImageUrls : [],
            current: buildupCredit(item.grade), writable: attemptId == null, allowPartial: true,
            target: { kind: 'buildup', configComponentId: component.configComponentId, step: step.step, itemNo: item.number },
          })
        }
      }
    }
    if (component.type === 'video' && component.video?.pauseEnabled) {
      for (const row of pauseRowsOf(component)) {
        if (row.pauseTimeMs == null) continue
        const response = component.video.pauseQuizResponses?.find(r => r.quizId === row.key && r.pauseTimeMs === row.pauseTimeMs)
        const gradingRow = { ...row, ...normalizePauseCurrent(response) }
        rows.push({
          key: `block:${component.configComponentId}:${row.pauseTimeMs}:${row.key}`, section: 'blockQuiz', no: row.no,
          label: component.label, current: gradingRow.current, writable: attemptId == null, allowPartial: row.allowPartial, gradingRow,
          target: { kind: 'blockQuiz', configComponentId: component.configComponentId, quizId: row.key, pauseTimeMs: row.pauseTimeMs },
        })
      }
    }
  }
  for (const note of reviewNotes.filter(n => n.status !== 'CANCELLED')) {
    for (const row of reviewNoteRowsOf(note)) {
      rows.push({
        key: `review:${note.setId}:${row.key}`, section: 'reviewNote', no: row.no, current: row.current,
        writable: note.canGrade, allowPartial: false, gradingRow: row,
        target: { kind: 'reviewNote', setId: note.setId, quizId: row.key },
      })
    }
  }
  if (attemptId == null && lesson.workbookGradingEnabled !== false) {
    for (const itemNo of REQUIRED_WORKBOOK_PROBLEM_NOS) {
      const grade = workbook.find(g => g.itemKind === 'PROBLEM' && g.itemNo === itemNo)
      rows.push({ key: `workbook:${itemNo}`, section: 'workbook', no: itemNo,
        current: grade?.partialCredit ?? (grade?.isCorrect == null ? null : grade.isCorrect ? 1 : 0),
        writable: true, allowPartial: false, target: { kind: 'workbook', itemNo } })
    }
  }
  for (const check of checks) {
    rows.push({ key: `essential:${check.checkNo}`, section: 'essentialCheck', no: check.checkNo,
      label: check.text ?? check.label, current: check.score, writable: true, allowPartial: false,
      target: { kind: 'essentialCheck', checkNo: check.checkNo } })
  }
  return HANDHELD_SECTIONS.flatMap(section => rows.filter(row => row.section === section))
}

interface SaveOptions {
  learnerId: number
  lessonId: number
  attemptId: number | null
  scope: string
  rows: HandheldRow[]
  store: HandheldDraftStore
  refresh: () => Promise<void>
  errorMessage: (error: unknown) => string
  failedOnly?: boolean
}

/** One batch per component/workbook/checks; single-row APIs run in visible order. */
export async function saveHandheldDrafts({ learnerId, lessonId, attemptId, scope, rows, store, refresh, errorMessage, failedOnly }: SaveOptions) {
  if (!store.getState().beginSave()) return
  const snapshot = store.getState().scopes[scope]
  const draft = { ...snapshot?.drafts }
  const targets = rows.filter(row => row.writable && draft[row.key] != null && (!failedOnly || snapshot?.failed[row.key]))
  const batches: HandheldRow[][] = []
  const grouped = new Map<string, HandheldRow[]>()
  for (const row of targets) {
    const target = row.target
    const batchKey = target.kind === 'buildup' ? `buildup:${target.configComponentId}`
      : target.kind === 'workbook' || target.kind === 'essentialCheck' ? target.kind : null
    if (batchKey == null) batches.push([row])
    else {
      let group = grouped.get(batchKey)
      if (!group) { group = []; grouped.set(batchKey, group); batches.push(group) }
      group.push(row)
    }
  }
  try {
    for (const batch of batches) {
      const target = batch[0].target
      try {
        if (target.kind === 'workbook') {
          await lcCoachingApi.gradeWorkbook(learnerId, { lessonId, items: batch.map(row => ({
            itemKind: 'PROBLEM', itemNo: row.no, isCorrect: draft[row.key] === 1, partialCredit: asCredit(draft[row.key]),
          })) })
        } else if (target.kind === 'essentialCheck') {
          await lcCoachingApi.rateEssentialChecks(learnerId, { lessonId, learningAttemptId: attemptId,
            ratings: batch.map(row => ({ checkNo: row.no, score: draft[row.key] })) })
        } else if (target.kind === 'buildup') {
          await lcCoachingApi.gradeBuildupSteps(learnerId, { lessonId, configComponentId: target.configComponentId,
            grades: batch.flatMap(row => row.target.kind === 'buildup' ? [{ step: row.target.step, itemNo: row.target.itemNo, grade: toBuildupGrade(draft[row.key]) }] : []) })
        } else if (target.kind === 'blockQuiz') {
          const value = asCredit(draft[batch[0].key])
          await lcCoachingApi.overrideQuizAnswer(learnerId, { lessonId, configComponentId: target.configComponentId,
            quizId: target.quizId, scope: 'pause', pauseTimeMs: target.pauseTimeMs, isCorrect: value === 1,
            ...(batch[0].allowPartial ? { partialCredit: value } : {}),
          })
        } else {
          await lcReviewNoteApi.gradeItem(learnerId, target.setId, target.quizId, { isCorrect: draft[batch[0].key] === 1, manualGrade: true })
        }
        for (const row of batch) store.getState().saved(scope, row.key, draft[row.key])
      } catch (error) {
        for (const row of batch) store.getState().failed(scope, row.key, errorMessage(error))
      }
    }
    // Unknown/stale targets must not silently disappear or be saved into another scope.
    for (const key of Object.keys(draft)) {
      if (!rows.some(row => row.key === key && row.writable)) store.getState().failed(scope, key, 'STALE_TARGET')
    }
    await refresh()
    store.getState().clearAcknowledged(scope)
  } finally {
    store.getState().endSave()
  }
}
